import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { fieldCls } from './styles';
import type { SelectOption } from './FilterSelect';

interface Props {
  values: string[];
  onChange: (v: string[]) => void;
  options: SelectOption[];
  /** Accessible label. */
  label: string;
  /** Summary shown when nothing is selected (e.g. "Any study"). Falls back to
   *  the label of a value:'' option if the caller supplies one. */
  placeholder?: string;
  title?: string;
  /** Shrink to content width (toolbar) vs full width (popover field). */
  auto?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Checkbox-dropdown multi-select — the many-value sibling of <FilterSelect>.
 * Same rectangular trigger shape as the native select, opening a checkbox list
 * (OR-within-a-filter). A value:'' option (e.g. "Any study") is treated as the
 * clear-all row, so callers can reuse the exact `options` array they pass to a
 * single <FilterSelect>. Selecting nothing means "any" — the callers omit the
 * query param entirely.
 */
export default function FilterMultiSelect({
  values,
  onChange,
  options,
  label,
  placeholder,
  title,
  auto,
  disabled,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A value:'' entry is the "Any…" / clear-all affordance, not a real checkbox.
  const clearOption = options.find((o) => o.value === '');
  const realOptions = options.filter((o) => o.value !== '');
  const anyLabel = placeholder || clearOption?.label || 'Any';
  const selected = new Set(values);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Preserve the option order so joined params / chips stay stable.
    onChange(realOptions.map((o) => o.value).filter((v) => next.has(v)));
  };

  const summary = (() => {
    if (values.length === 0) return anyLabel;
    const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
    if (values.length === 1) return labelFor(values[0]);
    return `${labelFor(values[0])} +${values.length - 1}`;
  })();

  return (
    <div ref={ref} className={`relative ${auto ? 'inline-block' : 'w-full'}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
        className={`${fieldCls} inline-flex min-h-[44px] items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 ${
          auto ? 'w-auto' : 'w-full'
        } ${values.length > 0 ? 'border-primary-500 dark:border-primary-500' : ''} ${className}`}
      >
        <span className={`min-w-0 truncate ${values.length === 0 ? 'text-gray-500 dark:text-gray-400' : ''}`}>
          {summary}
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          className="absolute left-0 z-30 mt-1 max-h-72 w-full min-w-[12rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <button
            type="button"
            role="option"
            aria-selected={values.length === 0}
            onClick={() => onChange([])}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <span
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                values.length === 0 ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 dark:border-gray-500'
              }`}
            >
              {values.length === 0 && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{anyLabel}</span>
          </button>

          {realOptions.length > 0 && <div className="my-1 border-t border-gray-100 dark:border-gray-700" />}

          {realOptions.map((o) => {
            const checked = selected.has(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <span
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                    checked ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 dark:border-gray-500'
                  }`}
                >
                  {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-200">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
